import { useState, useMemo, useEffect } from 'react';
import { QueryClient } from '@tanstack/react-query';
import {
  sitesApi,
  organizationsApi,
  extractApiError,
  AssignmentType,
  OWNER_ROLE_KEY,
  SiteAssignment,
  OrganizationMember,
  OrganizationInvitation,
} from '../../../lib/organizations';
import { TrendPeriodType } from '../../../lib/organizations';
import { rbacApi, type CompanyRoleView } from '../../../lib/rbac';
import { isOrganizationMember } from '../../../lib/members';
import { useToastStore } from '../../../stores/toast';
import { SiteDetailState, SiteDetailHandlers, SiteDetailPermissions, SiteDetailTab } from './types';

interface UseSiteDetailStateParams {
  companyId: string | undefined;
  siteId: string | undefined;
  memberId: string | undefined;
  /** 조직 RBAC 키(P4 ②) — `usePermissions()`에서 받은 값. 옛 `isOwner`(role 비교) 대신 자리마다 키 하나 */
  canManageSite: boolean;
  canAssign: boolean;
  canInvite: boolean;
  canManageApprovalLines: boolean;
  assignments: SiteAssignment[];
  members: OrganizationMember[];
  queryClient: QueryClient;
  /** 진입 시 열 탭(`?tab=` 쿼리에서 파생) — 마운트 초기값으로만 쓰인다. */
  initialDetailTab?: SiteDetailTab;
}

interface UseSiteDetailStateReturn {
  state: SiteDetailState;
  handlers: SiteDetailHandlers;
  permissions: SiteDetailPermissions;
}

/** 초대 역할 멀티셀렉트 기본값 — 멤버 관리 초대 모달(`useMembersState`)과 같은 값(시스템 역할 `member`). */
const DEFAULT_INVITE_ROLE_KEYS = ['member'];

/**
 * SiteDetail 페이지의 상태와 핸들러를 관리하는 커스텀 훅
 */
export function useSiteDetailState({
  companyId,
  siteId,
  memberId,
  canManageSite,
  canAssign,
  canInvite,
  canManageApprovalLines,
  assignments,
  members,
  queryClient,
  initialDetailTab = 'basic',
}: UseSiteDetailStateParams): UseSiteDetailStateReturn {
  const { showToast } = useToastStore();

  // === 상태 정의 ===

  // 접이식 섹션 상태
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    basicInfo: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // 위험요인 추이 기간
  const [trendPeriod, setTrendPeriod] = useState<TrendPeriodType>('daily');

  // 활성 탭
  const [activeTab, setActiveTab] = useState<'inspections' | 'reports' | 'tbm'>('inspections');

  // 에러 상태
  const [error, setError] = useState('');

  // 멤버 할당 모달
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignMode, setAssignMode] = useState<'select' | 'invite'>('select');
  const [setPrimaryOnAssign, setSetPrimaryOnAssign] = useState(false);

  // 초대 폼 상태 — 역할 축은 멤버 관리 초대 모달과 같다(P7-b-2: 「소유자로 초대」 토글 + 역할 멀티셀렉트, 기본 `member`).
  // 옛 select(기본 SAFETY_MANAGER)는 `role` enum과 함께 사라졌다 — 현장 초대의 기본 권한이 안전관리자에서 일반 멤버로
  // 바뀌는 정책 변화라 팀장 보고(2026-09-25) 항목.
  const [inviteIdentifier, setInviteIdentifier] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteAsOwner, setInviteAsOwner] = useState(false);
  const [inviteRoleKeys, setInviteRoleKeys] = useState<string[]>(DEFAULT_INVITE_ROLE_KEYS);
  const [inviteRoleOptions, setInviteRoleOptions] = useState<CompanyRoleView[]>([]);
  const [isLoadingInviteRoles, setIsLoadingInviteRoles] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  // 초대 폼이 열릴 때 조직 역할 후보를 싣는다(멤버 관리 초대 모달과 같은 규칙 — 소유자 역할은 토글이 맡으므로 뺀다).
  // 실패는 조용히 둔다(후보 없이도 기본 `member`로 초대는 된다).
  useEffect(() => {
    if (!showAssignModal || assignMode !== 'invite' || !companyId) return;
    let cancelled = false;
    setIsLoadingInviteRoles(true);
    rbacApi
      .getRoles(companyId)
      .then((roles) => {
        if (!cancelled) setInviteRoleOptions(roles.filter((r) => !r.isOwner));
      })
      .catch(() => {
        if (!cancelled) setInviteRoleOptions([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingInviteRoles(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showAssignModal, assignMode, companyId]);

  const toggleInviteRoleKey = (roleKey: string) => {
    setInviteRoleKeys((prev) => (prev.includes(roleKey) ? prev.filter((k) => k !== roleKey) : [...prev, roleKey]));
  };

  // 할당 해제
  const [unassignTarget, setUnassignTarget] = useState<SiteAssignment | null>(null);
  const [isUnassigning, setIsUnassigning] = useState(false);

  // 초대 관련
  const [isResending, setIsResending] = useState<string | null>(null);
  const [invitationCancelTarget, setInvitationCancelTarget] = useState<OrganizationInvitation | null>(null);
  const [isCancellingInvitation, setIsCancellingInvitation] = useState(false);

  // 담당자 상세 모달
  const [selectedAssignment, setSelectedAssignment] = useState<SiteAssignment | null>(null);

  // 상세 탭
  const [detailTab, setDetailTab] = useState<SiteDetailTab>(initialDetailTab);

  // === 핸들러 정의 ===

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setSelectedMemberId('');
    setAssignMode('select');
    setSetPrimaryOnAssign(false);
    setInviteIdentifier('');
    setInvitePhone('');
    setInviteAsOwner(false);
    setInviteRoleKeys(DEFAULT_INVITE_ROLE_KEYS);
  };

  const handleAssign = async () => {
    if (!companyId || !siteId || !selectedMemberId) return;

    setIsAssigning(true);
    try {
      await sitesApi.assignMember(companyId, siteId, {
        memberId: selectedMemberId,
        setPrimary: setPrimaryOnAssign,
      });
      queryClient.invalidateQueries({ queryKey: ['siteAssignments', siteId] });
      closeAssignModal();
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleInvite = async () => {
    if (!companyId || !siteId) return;

    const identifier = inviteIdentifier.trim();
    const phone = invitePhone.trim();

    if (!identifier && !phone) return;

    setIsInviting(true);
    try {
      const inviteData: { identifier?: string; phone?: string; roleKeys: string[]; siteId: string } = {
        roleKeys: inviteAsOwner ? [OWNER_ROLE_KEY] : inviteRoleKeys,
        siteId: siteId,
      };

      if (identifier) {
        inviteData.identifier = identifier;
      }
      if (phone) {
        inviteData.phone = phone;
      }

      const result = await organizationsApi.inviteMember(companyId, inviteData);

      if (result.type === 'MEMBER_ADDED') {
        queryClient.invalidateQueries({ queryKey: ['siteAssignments', siteId] });
        queryClient.invalidateQueries({ queryKey: ['organizationMembers', companyId] });
      } else if (result.invitation) {
        queryClient.invalidateQueries({ queryKey: ['siteInvitations', siteId] });
      }

      setInviteIdentifier('');
      setInvitePhone('');
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsInviting(false);
    }
  };

  const handleUnassign = async () => {
    if (!companyId || !siteId || !unassignTarget) return;

    setIsUnassigning(true);
    try {
      await sitesApi.unassignMember(companyId, siteId, unassignTarget.memberId);
      queryClient.invalidateQueries({ queryKey: ['siteAssignments', siteId] });
      setUnassignTarget(null);
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsUnassigning(false);
    }
  };

  const handleResendInvitation = async (invitation: OrganizationInvitation) => {
    if (!companyId) return;

    setIsResending(invitation.id);
    try {
      await organizationsApi.resendInvitation(companyId, invitation.id);
      queryClient.invalidateQueries({ queryKey: ['siteInvitations', siteId] });
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsResending(null);
    }
  };

  const handleCancelInvitation = async () => {
    if (!companyId || !invitationCancelTarget) return;

    setIsCancellingInvitation(true);
    try {
      await organizationsApi.cancelInvitation(companyId, invitationCancelTarget.id);
      queryClient.invalidateQueries({ queryKey: ['siteInvitations', siteId] });
      setInvitationCancelTarget(null);
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsCancellingInvitation(false);
    }
  };

  const handleUpdateAssignmentType = async (assignmentType: AssignmentType) => {
    if (!companyId || !siteId || !selectedAssignment) return;

    try {
      await sitesApi.updateAssignment(
        companyId,
        siteId,
        selectedAssignment.memberId,
        assignmentType
      );
      await queryClient.invalidateQueries({ queryKey: ['siteAssignments', siteId] });
      setSelectedAssignment(null);
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    }
  };

  // (옛 현장별 권한 오버라이드 handleUpdateAssignmentPermissions — 3상태 canManageInspections — 는 P7-a에서
  //  서버 컬럼과 함께 제거)

  // === 권한 계산 ===

  // 점검 담당자 후보 = **조직 구성원 중** 아직 이 현장에 미배정인 사람.
  // isOrganizationMember를 지나지 않으면 외부 인원(countsAsMember=false — 현장 담당자·외부 결재자용
  // Member)까지 후보에 서고, 그들은 로그인 계정이 없어 목록에 이름 없이 뜬다. 실제로 그렇게
  // 배정된 행이 운영에 생겼다(2026-08-11 사용자 제보 — 점검 담당자에 '-'). 점검 담당자는
  // 안전관리자 배정이라 조직에 초대된 사람만 대상이다.
  const unassignedMembers = members.filter(
    (m) => isOrganizationMember(m) && !assignments.some((a) => a.memberId === m.id)
  );

  const isPrimaryAssignee = assignments.some(
    (a) => a.memberId === memberId && a.assignmentType === 'PRIMARY'
  );

  // 서버 `SitesService.update`와 같은 OR 판정(site.manage ∥ 정 담당자, P4 ②)
  const canEdit = canManageSite || isPrimaryAssignee;

  // === 반환 ===

  const state: SiteDetailState = useMemo(
    () => ({
      expandedSections,
      toggleSection,
      trendPeriod,
      setTrendPeriod,
      activeTab,
      setActiveTab,
      error,
      setError,
      showAssignModal,
      setShowAssignModal,
      selectedMemberId,
      setSelectedMemberId,
      isAssigning,
      assignMode,
      setAssignMode,
      setPrimaryOnAssign,
      setSetPrimaryOnAssign,
      inviteIdentifier,
      setInviteIdentifier,
      invitePhone,
      setInvitePhone,
      inviteAsOwner,
      setInviteAsOwner,
      inviteRoleKeys,
      toggleInviteRoleKey,
      inviteRoleOptions,
      isLoadingInviteRoles,
      isInviting,
      unassignTarget,
      setUnassignTarget,
      isUnassigning,
      isResending,
      invitationCancelTarget,
      setInvitationCancelTarget,
      isCancellingInvitation,
      selectedAssignment,
      setSelectedAssignment,
      detailTab,
      setDetailTab,
    }),
    [
      expandedSections,
      trendPeriod,
      activeTab,
      error,
      showAssignModal,
      selectedMemberId,
      isAssigning,
      assignMode,
      setPrimaryOnAssign,
      inviteIdentifier,
      invitePhone,
      inviteAsOwner,
      inviteRoleKeys,
      inviteRoleOptions,
      isLoadingInviteRoles,
      isInviting,
      unassignTarget,
      isUnassigning,
      isResending,
      invitationCancelTarget,
      isCancellingInvitation,
      selectedAssignment,
      detailTab,
    ]
  );

  const handlers: SiteDetailHandlers = useMemo(
    () => ({
      handleAssign,
      handleInvite,
      closeAssignModal,
      handleUnassign,
      handleResendInvitation,
      handleCancelInvitation,
      handleUpdateAssignmentType,
    }),
    [companyId, siteId, selectedMemberId, setPrimaryOnAssign, inviteIdentifier, invitePhone, inviteAsOwner, inviteRoleKeys, unassignTarget, invitationCancelTarget, selectedAssignment]
  );

  const permissions: SiteDetailPermissions = useMemo(
    () => ({
      canManageSite,
      canAssign,
      canInvite,
      canManageApprovalLines,
      isPrimaryAssignee,
      canEdit,
      unassignedMembers,
    }),
    [canManageSite, canAssign, canInvite, canManageApprovalLines, isPrimaryAssignee, canEdit, unassignedMembers]
  );

  return { state, handlers, permissions };
}
